import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilState } from "recoil";
import { useAuth0 } from "@auth0/auth0-react";
import { API, API_BASEURL, buildUrl } from "../../../config";
import { fetcherGet, fetcherPost } from "../../../utils";
import { getToken } from "../../../utils/auth";
import { useClickOutside } from "../../../hooks";
import { currentSessionAtom, currentSessionLoadedAtom } from "../../../state/attendance/currentSessionAtom";

/** Inject the lr-pulse keyframe once into the document head. */
let pulseCssInjected = false;
function ensurePulseKeyframes() {
    if (pulseCssInjected || typeof document === "undefined") return;
    pulseCssInjected = true;
    const style = document.createElement("style");
    style.textContent = `
        @keyframes lr-pulse {
            0%   { transform: scale(1); opacity: 0.6; }
            100% { transform: scale(2.6); opacity: 0; }
        }
    `;
    document.head.appendChild(style);
}

function formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
    });
}

/**
 * Clock In / Clock Out control for the Navbar.
 * – Clock In: yellow (#F5C518) button
 * – Clock Out: black (#1A1A1A) button with a pulsing green dot,
 *   "Clock Out" primary line, and an optional "Expected stop at {time}" secondary line.
 * – Clicking Clock Out opens an inline popover for confirmation.
 * Reads / writes currentSessionAtom (Recoil). Loads the current session on mount.
 *
 * NO MUI — all styles are Tailwind + inline CSS for animation-specific rules.
 */
export function ClockControl() {
    ensurePulseKeyframes();

    const auth0 = useAuth0();
    const [session, setSession] = useRecoilState(currentSessionAtom);
    const [loaded, setLoaded] = useRecoilState(currentSessionLoadedAtom);
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const wrapRef = useRef<HTMLDivElement>(null);
    const closeConfirm = useCallback(() => setConfirmOpen(false), []);
    useClickOutside(wrapRef, closeConfirm);

    // Hydrate session state from API on first render
    useEffect(() => {
        if (loaded) return;

        let cancelled = false;

        const loadSession = async () => {
            try {
                const token = await getToken(auth0);
                const result = await fetcherGet(
                    buildUrl(API.ATTENDANCE_CURRENT_SESSION, {}, API_BASEURL),
                    token
                );
                if (!cancelled) {
                    setSession(result ?? null);
                    setLoaded(true);
                }
            } catch {
                if (!cancelled) setLoaded(true);
            }
        };

        loadSession();

        return () => {
            cancelled = true;
        };
    }, [loaded, auth0, setSession, setLoaded]);

    const handleClockIn = async () => {
        setLoading(true);
        try {
            const token = await getToken(auth0);
            const [result] = await fetcherPost(
                buildUrl(API.ATTENDANCE_CLOCK_IN, {}, API_BASEURL),
                token,
                {}
            );
            if (result) setSession(result);
        } finally {
            setLoading(false);
        }
    };

    const handleClockOutConfirm = async () => {
        setConfirmOpen(false);
        setLoading(true);
        try {
            const token = await getToken(auth0);
            const [result] = await fetcherPost(
                buildUrl(API.ATTENDANCE_CLOCK_OUT, {}, API_BASEURL),
                token,
                {}
            );
            if (result) setSession(null);
        } finally {
            setLoading(false);
        }
    };

    if (!loaded) return null;

    return (
        <div ref={wrapRef} className="relative flex items-center">
            {/* ── Clock In button ── */}
            {!session && (
                <button
                    type="button"
                    disabled={loading}
                    onClick={handleClockIn}
                    className="flex items-center gap-1.5 rounded-lg px-[18px] py-[9px] text-[13px] font-bold leading-none tracking-[0.2px] transition-[filter] hover:brightness-95 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: "#F5C518", color: "#1A1A1A", border: "none" }}
                >
                    {/* Clock icon (inline SVG) */}
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {loading ? "Clocking in…" : "Clock In"}
                </button>
            )}

            {/* ── Clock Out button ── */}
            {session && (
                <button
                    type="button"
                    disabled={loading}
                    onClick={() => setConfirmOpen((v) => !v)}
                    className="flex flex-col items-start rounded-lg px-[18px] py-[7px] leading-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: "#1A1A1A", border: "none" }}
                >
                    {/* Primary line */}
                    <span
                        className="flex items-center gap-[7px] text-[13px] font-bold tracking-[0.2px]"
                        style={{ color: "#FFFFFF" }}
                    >
                        {/* Pulsing green dot */}
                        <span className="relative inline-flex w-2 h-2 flex-shrink-0">
                            <span
                                className="absolute inset-0 rounded-full"
                                style={{
                                    background: "#43A047",
                                    animation: "lr-pulse 1.8s ease-out infinite",
                                }}
                            />
                            <span
                                className="absolute inset-0 rounded-full"
                                style={{ background: "#43A047" }}
                            />
                        </span>
                        {loading ? "Clocking out…" : "Clock Out"}
                    </span>

                    {/* Secondary line — expected stop */}
                    {session.expectedStop && (
                        <span
                            className="mt-[1px] text-[11px] font-medium"
                            style={{ color: "#B9B9B9" }}
                        >
                            Expected stop at {formatTime(session.expectedStop)}
                        </span>
                    )}
                </button>
            )}

            {/* ── Confirmation popover ── */}
            {confirmOpen && (
                <div
                    className="absolute top-[calc(100%+8px)] right-0 z-50 w-60 rounded-[10px] p-4"
                    style={{
                        background: "#FFFFFF",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
                        border: "1px solid #EEE",
                    }}
                >
                    <p
                        className="m-0 text-[13.5px] font-semibold leading-[1.4]"
                        style={{ color: "#1A1A1A" }}
                    >
                        Are you sure you want to clock out?
                    </p>
                    <div className="flex gap-2 mt-[14px]">
                        <button
                            type="button"
                            onClick={() => setConfirmOpen(false)}
                            className="flex-1 rounded-[7px] py-2 text-[12.5px] font-semibold cursor-pointer transition-colors hover:bg-gray-50"
                            style={{
                                background: "#FFFFFF",
                                color: "#1A1A1A",
                                border: "1.5px solid #555",
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleClockOutConfirm}
                            className="flex-1 rounded-[7px] py-2 text-[12.5px] font-semibold text-white cursor-pointer transition-opacity hover:opacity-90"
                            style={{ background: "#1A1A1A", border: "none" }}
                        >
                            Clock Out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
